import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { AuraMark } from "@/components/AuraMark";
import { Button } from "@/components/ui/button";
import { CheckCircle2, XCircle } from "lucide-react";

export default function PaymentSuccess() {
  const nav = useNavigate();
  const { checkAuth } = useAuth();
  const [status, setStatus] = useState("checking");
  const tries = useRef(0);

  useEffect(() => {
    const sid = new URLSearchParams(window.location.search).get("session_id");
    if (!sid) { setStatus("error"); return; }
    const poll = async () => {
      try {
        const r = await api.get(`/payments/status/${sid}`);
        if (r.data.payment_status === "paid") {
          setStatus("paid"); await checkAuth(); return;
        }
        if (["failed", "expired"].includes(r.data.payment_status)) { setStatus("error"); return; }
      } catch (e) {}
      tries.current += 1;
      if (tries.current > 12) { setStatus("timeout"); return; }
      setTimeout(poll, 2000);
    };
    poll();
  }, [checkAuth]);

  return (
    <div className="min-h-screen flex items-center justify-center p-8">
      <div className="tracing-border rounded-2xl p-12 max-w-md w-full text-center">
        {status === "checking" && (<>
          <AuraMark className="w-14 h-14 mx-auto mb-6 floaty" />
          <h1 className="font-display font-bold text-2xl text-slate-50">Confirming your payment…</h1>
          <p className="text-slate-400 mt-3">Hang tight while we verify with Stripe and top up your credits.</p>
        </>)}
        {status === "paid" && (<>
          <CheckCircle2 className="w-16 h-16 text-cyan mx-auto mb-6" />
          <h1 className="font-display font-bold text-2xl text-slate-50">You're all set!</h1>
          <p className="text-slate-400 mt-3">Your plan is active and credits are in your account.</p>
          <Button data-testid="success-studio-btn" onClick={() => nav("/dashboard")} className="mt-8 rounded-full bg-cyan-400 text-black hover:bg-cyan-300 font-semibold px-7 h-11">Go to studio</Button>
        </>)}
        {(status === "error" || status === "timeout") && (<>
          <XCircle className="w-16 h-16 text-red-400 mx-auto mb-6" />
          <h1 className="font-display font-bold text-2xl text-slate-50">{status === "timeout" ? "Still processing" : "Payment issue"}</h1>
          <p className="text-slate-400 mt-3">{status === "timeout" ? "Your payment is taking longer than usual. Check your profile shortly." : "We couldn't confirm the payment."}</p>
          <Button data-testid="success-retry-btn" onClick={() => nav("/pricing")} className="mt-8 rounded-full bg-white/10 hover:bg-white/15 text-slate-50 px-7 h-11">Back to pricing</Button>
        </>)}
      </div>
    </div>
  );
}
